import { useLang } from 'hooks/lang';

import {
  calculateProjectCPI,
  calculateProjectTotal,
  calculateServiceCost,
  getServiceName,
  shouldShowProjectCPI,
} from 'entities';

import { Column, Alert, Row, IconButton, DropdownMenu, DropdownMenuItem } from 'components/Blocks';
import { IconInput } from 'components/Inputs';
import { ArrowUpIcon, PencilIcon, SearchIcon, SortIcon } from 'components/Icons';

import { SingleService } from './SingleService';
import { CPIHelpModal } from './CPIHelpModal';

import { useProjectProfile } from 'pages/Projects/ProjectProfile/context';
import { useProjectOption } from '../../options';

import { useFormControlV2 } from 'hooks';

import { formatCurrency, isSubstring } from 'utils/text';
import { quickSwitch } from 'utils/conditions';
import { sortByProperty } from 'utils/array';
import { cn } from 'styles';

const sortProps = ['name', 'value', 'outsourced'] as const;

type SortConfig = {
  ref: (typeof sortProps)[number];
  direction: 'ASC' | 'DESC' | null;
};

const nextDirection = (current: SortConfig['direction']) => {
  return quickSwitch(current, [
    { is: 'ASC', then: 'DESC' },
    { is: 'DESC', then: null },
    { is: null, then: 'ASC' },
  ]) as SortConfig['direction'];
};

export function ProjectCosts() {
  const { t } = useLang();
  const { project } = useProjectProfile();

  const manageCosts = useProjectOption('MANAGE_COSTS');

  const [{ search, sort }, { handleChange, dispatch }] = useFormControlV2({
    search: '',

    sort: {
      ref: 'name',
      direction: null,
    } as SortConfig,
  });

  const repriceFlowInitiated =
    project?.reprice === 'PENDING_APPROVAL' || project?.reprice === 'REQUESTED';

  const changeSort = (ref: SortConfig['ref']) => {
    dispatch({
      sort: ({ sort }) => ({
        ref,
        direction: nextDirection(sort.ref !== ref ? null : sort.direction),
      }),
    });
  };

  const baseServices = project.services
    .map((service) => ({
      ...service,
      name: getServiceName(service.id, project.tasks),
      value: Number(calculateServiceCost(service)),
    }))
    .filter((service) => isSubstring(search, service.name));

  const services = sort.direction
    ? sortByProperty(baseServices, sort.ref, { [sort.ref]: sort.direction })
    : baseServices;

  return (
    <Column gap="3">
      {repriceFlowInitiated && <Alert level="info">{t('repricing-flow-current-active')}</Alert>}

      <Row gap="2">
        <span className="mr-auto font-medium">Total</span>

        {manageCosts.isEnabled && (
          <IconButton
            onClick={manageCosts.action}
            className="text-primary"
            tooltipContent={t('manage-costs')}
          >
            <PencilIcon size={12} />
          </IconButton>
        )}

        <strong className="text-xl text-primary phone:text-lg">
          {formatCurrency(calculateProjectTotal(project.services), project.currency)}
        </strong>
      </Row>

      {shouldShowProjectCPI(project) && (
        <Row gap="2">
          <span className="font-medium">CPI</span>

          <CPIHelpModal
            cpi={formatCurrency(calculateProjectCPI(project.services), project.currency)}
          />

          <strong className="ml-auto text-primary phone:text-lg">
            {formatCurrency(calculateProjectCPI(project.services), project.currency)}
          </strong>
        </Row>
      )}

      {project.services.length > 1 && (
        <Row>
          <IconInput icon={<SearchIcon />} value={search} onChange={handleChange} name="search" />

          <DropdownMenu
            title={t('sort-by')}
            align="end"
            className="min-w-48"
            trigger={
              <IconButton size="small" className={sort.direction ? 'text-primary' : ''}>
                <SortIcon />
              </IconButton>
            }
          >
            {sortProps.map((prop) => (
              <DropdownMenuItem
                key={prop}
                closeOnClick={false}
                onClick={() => changeSort(prop)}
                className={
                  sort.direction && sort.ref === prop ? 'text-primary focus:text-primary' : ''
                }
              >
                <span className="mr-auto">{t(prop)}</span>

                {sort.ref === prop && sort.direction && (
                  <ArrowUpIcon
                    className={cn(
                      'transition-transform',
                      sort.direction === 'DESC' && 'rotate-180',
                    )}
                  />
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenu>
        </Row>
      )}

      <Column gap="3" className="min-h-80 pb-6">
        {services.map((service) => (
          <SingleService
            service={service}
            key={`cost-${service.id}`}
            enableTaskOpen
          ></SingleService>
        ))}
      </Column>
    </Column>
  );
}
